import React, { useState } from "react";
import "./AgeGroupForm.css";
import TextFieldInput from "../../../Atoms/Inputs/TextField/TextFieldInput";
import PrimaryButton from "../../../Atoms/Buttons/Primary/PrimaryButton";
import { Formik } from "formik";
import * as Yup from "yup";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const AgeGroupSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .required("Name is required"),
  minMembers: Yup.number()
    .required("Min members is required")
    .positive("Must be positive")
    .integer("Must be a whole number"),
  maxMembers: Yup.number()
    .required("Max members is required")
    .positive("Must be positive")
    .integer("Must be a whole number")
    .test(
      "max-greater-than-min",
      "Max members must be greater than or equal to min members",
      function (value) {
        const { minMembers } = this.parent;
        return !minMembers || !value || value >= minMembers;
      }
    ),
  minBirthday: Yup.date()
    .required("Min birthday is required")
    .typeError("Invalid date"),
  maxBirthday: Yup.date()
    .required("Max birthday is required")
    .typeError("Invalid date")
    .test(
      "max-after-min",
      "Max birthday must be after min birthday",
      function (value) {
        const { minBirthday } = this.parent;
        return (
          !minBirthday || !value || dayjs(value).isAfter(dayjs(minBirthday))
        );
      }
    ),
});

function AgeGroupForm({
  onSubmit,
  onCancel,
  initialValues = null,
  loading = false,
}) {
  const isEdit = !!initialValues;

  const defaultValues = {
    name: "",
    minMembers: "",
    maxMembers: "",
    minBirthday: null,
    maxBirthday: null,
  };

  const formInitialValues = initialValues
    ? {
        name: initialValues.name || "",
        minMembers: initialValues.minMembers || "",
        maxMembers: initialValues.maxMembers || "",
        minBirthday: initialValues.minBirthday
          ? dayjs(initialValues.minBirthday)
          : null,
        maxBirthday: initialValues.maxBirthday
          ? dayjs(initialValues.maxBirthday)
          : null,
      }
    : defaultValues;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Formik
        initialValues={formInitialValues}
        validationSchema={AgeGroupSchema}
        enableReinitialize={isEdit}
        onSubmit={(values) => {
          const submitData = {
            name: values.name,
            minMembers: parseInt(values.minMembers),
            maxMembers: parseInt(values.maxMembers),
            minBirthday: values.minBirthday
              ? values.minBirthday.toISOString()
              : null,
            maxBirthday: values.maxBirthday
              ? values.maxBirthday.toISOString()
              : null,
          };
          onSubmit(submitData);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form className="ageGroupForm" onSubmit={handleSubmit}>
            <div className="formField">
              <TextFieldInput
                fullWidth
                name="name"
                type="text"
                placeholder="Enter age group name"
                className="ageGroupInput"
                label="Age Group Name"
                Required={true}
                change={handleChange}
                blur={handleBlur}
                newValue={values.name}
              />
              {errors.name && touched.name && (
                <p className="error">{errors.name}</p>
              )}
            </div>

            <div className="formFieldRow">
              <div className="formField">
                <TextFieldInput
                  fullWidth
                  name="minMembers"
                  type="number"
                  placeholder="Min members"
                  className="ageGroupInput"
                  label="Min Members"
                  Required={true}
                  change={handleChange}
                  blur={handleBlur}
                  newValue={values.minMembers}
                  inputProps={{ min: "1", step: "1" }}
                />
                {errors.minMembers && touched.minMembers && (
                  <p className="error">{errors.minMembers}</p>
                )}
              </div>

              <div className="formField">
                <TextFieldInput
                  fullWidth
                  name="maxMembers"
                  type="number"
                  placeholder="Max members"
                  className="ageGroupInput"
                  label="Max Members"
                  Required={true}
                  change={handleChange}
                  blur={handleBlur}
                  newValue={values.maxMembers}
                  inputProps={{ min: "1", step: "1" }}
                />
                {errors.maxMembers && touched.maxMembers && (
                  <p className="error">{errors.maxMembers}</p>
                )}
              </div>
            </div>

            <div className="formFieldRow">
              <div className="formField">
                <DatePicker
                  label="Min Birthday"
                  value={values.minBirthday}
                  onChange={(newValue) => {
                    setFieldValue("minBirthday", newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: errors.minBirthday && touched.minBirthday,
                      helperText:
                        errors.minBirthday && touched.minBirthday
                          ? errors.minBirthday
                          : "",
                    },
                  }}
                />
              </div>

              <div className="formField">
                <DatePicker
                  label="Max Birthday"
                  value={values.maxBirthday}
                  onChange={(newValue) => {
                    setFieldValue("maxBirthday", newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: errors.maxBirthday && touched.maxBirthday,
                      helperText:
                        errors.maxBirthday && touched.maxBirthday
                          ? errors.maxBirthday
                          : "",
                    },
                  }}
                />
              </div>
            </div>

            <div className="formActions">
              <PrimaryButton
                classBtn={"center cancelButton"}
                text={"Cancel"}
                style={{
                  textTransform: "capitalize",
                  borderRadius: "5px",
                  color: "var(--primary)",
                  backgroundColor: "white",
                  border: "1px solid var(--primary)",
                  height: "40px",
                  padding: "0 20px",
                  minWidth: "100px",
                }}
                click={(e) => {
                  e.preventDefault();
                  onCancel();
                }}
              />
              <PrimaryButton
                classBtn={"center submitButton"}
                text={isEdit ? "Update Age Group" : "Add Age Group"}
                type="submit"
                loading={loading}
                disabled={loading}
                style={{
                  textTransform: "capitalize",
                  borderRadius: "5px",
                  color: "white",
                  backgroundColor: "var(--primary)",
                  height: "40px",
                  padding: "0 20px",
                  minWidth: "140px",
                }}
              />
            </div>
          </form>
        )}
      </Formik>
    </LocalizationProvider>
  );
}

export default AgeGroupForm;
